import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, ShieldCheck, Zap, TrendingUp, ClipboardList, LineChart, ShoppingCart, CheckCircle2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Navbar } from '@/components/layout/Navbar';
import { api } from '@/lib/api-client';
import type { HomepageStats } from '@shared/types';
export function HomePage() {
  const { data: stats } = useQuery({
    queryKey: ['homepage-stats'],
    queryFn: () => api<HomepageStats>('/api/homepage/stats'),
  });

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      {/* Hero Section */}
      <section className="py-16 lg:py-24 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-6">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
              Livestock startup packages
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Complete equipment lists for poultry, fish, and small livestock. 
              Calculated for your budget and space.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button size="lg" className="rounded-md" asChild>
                <Link to="/wizard">Get started <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button size="lg" variant="outline" className="rounded-md" asChild>
                <Link to="/catalog">View Catalog</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
      {/* Features Grid */}
      <section className="py-16 bg-muted/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            <FeatureCard
              icon={<BarChart3 className="h-5 w-5" />}
              title="Verified ROI"
              description="Prices and returns based on local market rates."
            />
            <FeatureCard
              icon={<ShieldCheck className="h-5 w-5" />}
              title="Standard configurations"
              description="Tested feed, animal density, and equipment ratios."
            />
            <FeatureCard
              icon={<TrendingUp className="h-5 w-5" />}
              title="Scalable"
              description="Expand your operation by adding more units."
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 border-t border-b bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="space-y-2">
            <h2 className="text-2xl font-bold tracking-tight">How it works</h2>
            <p className="text-sm text-muted-foreground">Get recommendations and place your order in 4 steps.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            <StepCard
              number={1}
              icon={<ClipboardList className="h-6 w-6" />}
              title="Answer questions"
              description="Tell us about your livestock type, budget, and space."
            />
            <StepCard
              number={2}
              icon={<LineChart className="h-6 w-6" />}
              title="View packages"
              description="See starter, recommended, and pro packages with ROI."
            />
            <StepCard
              number={3}
              icon={<ShoppingCart className="h-6 w-6" />}
              title="Add to cart"
              description="Select items and adjust quantities as needed."
            />
            <StepCard
              number={4}
              icon={<CheckCircle2 className="h-6 w-6" />}
              title="Check out"
              description="Enter shipping and payment to complete order."
            />
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 border-t bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <StatItem label="Active Farms" value={stats?.activeFarms.toLocaleString() || '—'} />
            <StatItem label="Registered Users" value={stats?.registeredUsers.toLocaleString() || '—'} />
            <StatItem label="System ROI" value={stats ? `${stats.systemROI}%` : '—'} />
            <StatItem label="Support Hubs" value={stats?.supportHubs.toString() || '—'} />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 border-t bg-muted/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="space-y-2">
            <h2 className="text-2xl font-bold tracking-tight">What farmers are saying</h2>
            <p className="text-sm text-muted-foreground">Real feedback from customers who started with FarmPack.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <QuoteCard
              quote="I used the fish package list exactly as shown. My setup cost stayed within budget and I sold my first batch in month five."
              author="Kojo A., Kumasi"
            />
            <QuoteCard
              quote="The poultry plan made it clear what to buy first. I skipped extras and focused on essentials, which helped me avoid waste."
              author="Mariam U., Abuja"
            />
            <QuoteCard
              quote="The support team explained feed timing and vaccine order in plain language. That made daily work much easier for us."
              author="Daniel E., Accra"
            />
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <TrustMetric label="6-month farm success" value="78%" />
            <TrustMetric label="Recommendation acceptance" value="60%+" />
            <TrustMetric label="Average setup variance" value="<10%" />
          </div>
        </div>
      </section>
    </div>
  );
}
function FeatureCard({ icon, title, description }: { icon: React.ReactNode, title: string, description: string }) {
  return (
    <Card className="rounded-md border bg-background shadow-sm">
      <CardContent className="pt-6 space-y-3">
        <div className="text-primary">{icon}</div>
        <h3 className="text-base font-bold">{title}</h3>
        <p className="text-sm text-muted-foreground leading-normal">{description}</p>
      </CardContent>
    </Card>
  );
}
function StatItem({ value, label }: { value: string, label: string }) {
  return (
    <div className="space-y-1">
      <div className="text-2xl font-bold text-foreground">{value}</div>
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</div>
    </div>
  );
}

function QuoteCard({ quote, author }: { quote: string; author: string }) {
  return (
    <Card className="rounded-md border bg-background shadow-sm">
      <CardContent className="pt-6 space-y-4">
        <p className="text-sm text-foreground leading-relaxed">"{quote}"</p>
        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{author}</p>
      </CardContent>
    </Card>
  );
}

function TrustMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-background px-4 py-5">
      <div className="text-xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
    </div>
  );
}

function StepCard({ number, icon, title, description }: { number: number; icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="flex items-center justify-center w-12 h-12 rounded-full border-2 border-primary bg-primary/10 text-primary font-bold text-lg">
          {number}
        </div>
        <div className="text-primary">{icon}</div>
      </div>
      <h3 className="text-base font-bold">{title}</h3>
      <p className="text-sm text-muted-foreground leading-normal">{description}</p>
    </div>
  );
}